import { useEffect } from "react";
import { DEFAULT_SOCIAL_IMAGE, SITE_ORIGIN } from "../lib/seo";

export { SITE_ORIGIN };

const siteUrl = (value, fallbackPath) => {
  const candidate = value || fallbackPath;
  try {
    const url = new URL(candidate, SITE_ORIGIN);
    return `${SITE_ORIGIN}${url.pathname}${url.search}`;
  } catch {
    return `${SITE_ORIGIN}${fallbackPath}`;
  }
};

const upsertMeta = (selector, attributes) => {
  let element = document.head.querySelector(selector);
  if (!element) {
    element = document.createElement("meta");
    document.head.appendChild(element);
  }
  Object.entries(attributes).forEach(([key, value]) => element.setAttribute(key, value));
};

export const PageMeta = ({ title, description, canonical, image, imageAlt = "Behaviour Operating System", type = "website", publishedTime, ogTitle, ogDescription, author, robots = "index,follow", schemas = [] }) => {
  useEffect(() => {
    const url = siteUrl(canonical, window.location.pathname);
    const resolvedImage = siteUrl(image || DEFAULT_SOCIAL_IMAGE, "/assets/logo-stacked.png");
    document.title = title;
    upsertMeta('meta[name="description"]', { name: "description", content: description });
    upsertMeta('meta[name="robots"]', { name: "robots", content: robots });
    upsertMeta('meta[property="og:title"]', { property: "og:title", content: ogTitle || title });
    upsertMeta('meta[property="og:description"]', { property: "og:description", content: ogDescription || description });
    upsertMeta('meta[property="og:type"]', { property: "og:type", content: type });
    upsertMeta('meta[property="og:url"]', { property: "og:url", content: url });
    upsertMeta('meta[property="og:image"]', { property: "og:image", content: resolvedImage });
    upsertMeta('meta[property="og:image:alt"]', { property: "og:image:alt", content: imageAlt });
    upsertMeta('meta[property="og:site_name"]', { property: "og:site_name", content: "Behaviour Operating System" });
    upsertMeta('meta[property="og:locale"]', { property: "og:locale", content: "en_AU" });
    upsertMeta('meta[name="twitter:card"]', { name: "twitter:card", content: "summary_large_image" });
    upsertMeta('meta[name="twitter:title"]', { name: "twitter:title", content: ogTitle || title });
    upsertMeta('meta[name="twitter:description"]', { name: "twitter:description", content: ogDescription || description });
    upsertMeta('meta[name="twitter:image"]', { name: "twitter:image", content: resolvedImage });
    upsertMeta('meta[name="twitter:image:alt"]', { name: "twitter:image:alt", content: imageAlt });
    if (publishedTime) {
      upsertMeta('meta[property="article:published_time"]', { property: "article:published_time", content: publishedTime });
    } else {
      document.head.querySelector('meta[property="article:published_time"]')?.remove();
    }
    if (author) {
      upsertMeta('meta[name="author"]', { name: "author", content: author });
      upsertMeta('meta[property="article:author"]', { property: "article:author", content: author });
    } else {
      document.head.querySelector('meta[name="author"]')?.remove();
      document.head.querySelector('meta[property="article:author"]')?.remove();
    }
    let link = document.head.querySelector('link[rel="canonical"]');
    if (!link) {
      link = document.createElement("link");
      link.rel = "canonical";
      document.head.appendChild(link);
    }
    link.href = url;
    document.head.querySelectorAll('script[data-bos-jsonld]').forEach((node) => node.remove());
    schemas.forEach((schema, index) => {
      const script = document.createElement("script");
      script.type = "application/ld+json";
      script.dataset.bosJsonld = String(index + 1);
      script.textContent = JSON.stringify({ "@context": "https://schema.org", ...schema });
      document.head.appendChild(script);
    });
  }, [title, description, canonical, image, imageAlt, type, publishedTime, ogTitle, ogDescription, author, robots, schemas]);
  return null;
};
